import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import Close from "@tIntermatic/Icons/Close";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 100,
        isAutoSize: true,
        cssOverrides: {
            scrim: css`
                display: flex;
                justify-content: center;
                width: 40%;
                height: 170px;
                margin: auto;
                right: 0;
                top: initial;
                border: 0.3px solid #ccc;
                box-shadow: 3px 10px 10px #666;
                z-index: 400;
                border-radius: 5px;
                > div[class^="ContentContainer"] {
                    width: 100%;
                    > div[class^="ContentBody"] {
                        position: static;
                        overflow: hidden;
                    }
                }

                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 180px;
                                width: 100%;
                            `,
                            css`
                                height: 180px;
                                width: 100%;
                            `,
                            css`
                                width: 45%;
                                height: 180px;
                            `,
                            css`
                                width: 45%;
                                height: 180px;
                            `,
                            css`
                                width: 45%;
                                height: 180px;
                            `,
                        ],
                        "min",
                    );
                }}
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 15px;
                width: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 20px;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
                > div[class^="GridWrapper"] {
                    > div[class^="GridOffset"] {
                        display: block;
                    }
                }
            `,
            drawerBody: css`
                background-color: #ffffff;
                box-shadow: inset 0 5px 4px 0 rgb(0 0 0 / 10%);
                width: 100%;
                display: block;
                height: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                height: 200px;
                                width: 100%;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: 11,
        css: css`
            max-height: 90px;
            overflow-y: auto;
            margin-top: 10px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 1;
                            padding: 0 5px;
                        `,
                        css`
                            order: 1;
                            padding: 0 5px;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    messageText: {
        css: css`
            color: #030303;
            font-size: 18px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 30px;
            margin-bottom: 1rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 19px;
                            color: #252525;
                            text-align: center;
                        `,
                        css`
                            font-size: 16px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 19px;
                            color: #252525;
                            text-align: center;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    acceptGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 3;
                            justify-content: center;
                        `,
                        css`
                            order: 3;
                            justify-content: center;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    closeGridItem: {
        css: css`
            position: absolute;
            top: 8px;
            right: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            top: 0;
                            right: 30px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    closeButton: {
        css: css`
            border: none;
            background: none;
            color: black;
            > span {
                color: black !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                right: 12px;
                            `,
                            null,
                            null,
                        ],
                        "minMax",
                    )}
            }
            :hover {
                background: none;
                border-color: none;
            }
        `,
    },
    acceptButton: {
        css: css`
            box-sizing: border-box;
            padding: 0 35px;
            border: 1px solid #cccccc;
            background-color: #ffffff;
            width: 100%;
            > span[class^="TypographyStyle"] {
                color: #33333a;
                font-size: 12px;
                font-weight: 600;
                letter-spacing: 2px;
                line-height: 20px;
                text-align: center;
            }
            &:hover {
                border: 1px solid #cccccc;
                background-color: #ffffff;
            }
        `,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 2147483647 });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.closeGridItem}>
                    <Button
                        {...styles.closeButton}
                        onClick={closeHandler}
                        aria-label={translate("Close")}
                        data-test-selector="cookiePolicy_ClosePolicy"
                    >
                        <ButtonIcon src={X} />
                    </Button>
                </GridItem>
                <GridItem {...styles.acceptGridItem}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
